/**
 * Design-system ConfirmDialog · replaces native `window.confirm` /
 * `window.alert` with an in-app modal that matches the app's visual
 * language (plasma border, surface background, modal z-layer).
 *
 * Built on `@radix-ui/react-dialog`, which provides the focus trap,
 * Escape handling, outside-click dismissal, and scroll lock for free.
 *
 * Two consumption shapes:
 *
 *   1. Imperative via the `useConfirm()` hook (recommended for one-shot
 *      action confirmations like Archive / Delete):
 *
 *        const confirm = useConfirm();
 *        async function onArchive() {
 *          const ok = await confirm({
 *            title: 'Archive Elin Bergström?',
 *            body: 'You can restore it from the archived filter.',
 *            confirmLabel: 'Archive',
 *            tone: 'danger',
 *          });
 *          if (!ok) return;
 *          // proceed
 *        }
 *
 *   2. Declarative `<ConfirmDialog open={...} />` for callers that
 *      already manage their own open state.
 *
 * The provider lives at the app root (see `app/providers.tsx`).
 * Pressing Escape, clicking the backdrop, or clicking Cancel resolves
 * to `false`; clicking the confirm button resolves to `true`.
 */
import * as Dialog from '@radix-ui/react-dialog';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Button } from '../button/button';

import styles from './confirm-dialog.module.css';

export type ConfirmTone = 'default' | 'danger';

export interface ConfirmOptions {
  title: string;
  /** Long-form explanation. Optional · keeps the dialog compact for
   *  simple yes/no prompts. */
  body?: ReactNode;
  /** Label for the confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /** `danger` switches the confirm button to the danger variant and
   *  paints the card border red. Use for destructive actions. */
  tone?: ConfirmTone;
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        /* Radix calls this for Escape, outside-click, and the close
           button. Any close request that isn't an explicit confirm is
           a cancel. */
        if (!next) onCancel();
      }}
    >
      <Dialog.Portal>
        {/* Backdrop doubles as the centering container · clicking the
            dimmed area (outside the card) dismisses via Radix. */}
        <Dialog.Overlay className={styles.backdrop}>
          <Dialog.Content
            className={[styles.card, tone === 'danger' ? styles.cardDanger : '']
              .filter(Boolean)
              .join(' ')}
            data-testid="confirm-dialog"
            /* No body means no description. Radix auto-wires
               aria-describedby to its generated descriptionId, so when
               there's no <Dialog.Description> we override it to undefined
               to avoid dangling to a missing id. When a body exists we
               leave the prop off so Radix's wiring stands. */
            {...(body ? {} : { 'aria-describedby': undefined })}
            /* Focus the confirm button on open so the user can press
               Enter immediately. Cancel stays reachable via Shift+Tab. */
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              confirmBtnRef.current?.focus();
            }}
          >
            <Dialog.Title className={styles.title}>{title}</Dialog.Title>
            {body ? <Dialog.Description className={styles.body}>{body}</Dialog.Description> : null}
            <div className={styles.actions}>
              <Button
                variant="ghost"
                size="md"
                onClick={onCancel}
                data-testid="confirm-dialog-cancel"
              >
                {cancelLabel}
              </Button>
              <Button
                ref={confirmBtnRef}
                variant="primary"
                size="md"
                onClick={onConfirm}
                data-testid="confirm-dialog-confirm"
              >
                {confirmLabel}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ------------------------------------------------------------------ *
 * Imperative hook · `useConfirm()` returns a function that resolves
 * to a boolean. Backed by a context provider mounted at the app root.
 * ------------------------------------------------------------------ */

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingState {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingState | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending({ options, resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    pending?.resolve(true);
    setPending(null);
  }, [pending]);

  const handleCancel = useCallback(() => {
    pending?.resolve(false);
    setPending(null);
  }, [pending]);

  /* Memoise the context value so consumers don't re-render just
     because the provider re-rendered for an internal state update. */
  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={pending !== null}
        title={pending?.options.title ?? ''}
        body={pending?.options.body}
        confirmLabel={pending?.options.confirmLabel}
        cancelLabel={pending?.options.cancelLabel}
        tone={pending?.options.tone}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
}

/**
 * Imperative confirm. Throws if called outside <ConfirmDialogProvider>
 * so the missing wiring is caught at the first call site instead of
 * silently no-op'ing.
 */
// eslint-disable-next-line react-refresh/only-export-components -- provider + hook intentionally colocated
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmDialogProvider>');
  }
  return ctx;
}
